import {
  type CampaignQuest,
  type CampaignQuestState,
  type QuestCompletion,
  type QuestCompletions,
  mapCampaignQuestType,
} from "@/components/QuestsV2/types";
import type { PredictedStatsResponse } from "@/domain/gems-game/domain/lib/Sweepstake";
import { submitQuestCompletion } from "@/hooks/quest/useSubmitQuest";
import { useAccessToken } from "@/hooks/useAccessToken";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import {
  linkedAccountPlatforms,
  openAuthorizeUrl,
} from "@/utils/linkedaccounts/linkedAccounts";
import { authorizedFetcherGet } from "@/utils/request";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import type { KeyedMutator } from "swr";
import type { GameData } from "./types";
import { questCompletedInLatestPeriod } from "./utils";

export const useGameDetails = (
  gameData: GameData,
  completedQuests: QuestCompletion[] | undefined,
  invalidateQuestCompletions: KeyedMutator<QuestCompletions>,
) => {
  const getAccessToken = useAccessToken();

  // Check if quests are already enriched with weeklyPoints
  const needsEnrichment = useMemo(() => {
    return gameData.quests.some((quest) => quest.weeklyPoints === undefined);
  }, [gameData.quests]);

  // Only fetch predicted stats if enrichment is needed
  const { data: predictedStats } = useSWR<PredictedStatsResponse>(
    needsEnrichment ? "/v3/rewards/sweepstakes/user-stats/predicted" : null,
    (path: string) =>
      authorizedFetcherGet<PredictedStatsResponse>(path, getAccessToken),
  );

  // Map quest externalId to weeklyPoints for quick lookup (only when needed)
  const questWeeklyPointsMap = useMemo(() => {
    if (!needsEnrichment || !predictedStats?.games)
      return new Map<string, number>();

    const pointsMap = new Map<string, number>();
    for (const game of predictedStats.games) {
      for (const quest of game.quests) {
        if (quest.weeklyPoints !== undefined) {
          pointsMap.set(quest.externalId, quest.weeklyPoints);
        }
      }
    }
    return pointsMap;
  }, [predictedStats, needsEnrichment]);

  // Enrich gameData quests with weekly points only if needed
  const enrichedGameData = useMemo(() => {
    if (!needsEnrichment) {
      // Data already enriched, return as-is
      return gameData;
    }

    return {
      ...gameData,
      quests: gameData.quests.map((quest) => ({
        ...quest,
        weeklyPoints: questWeeklyPointsMap.get(quest.externalId) ?? undefined,
      })),
    };
  }, [gameData, questWeeklyPointsMap, needsEnrichment]);

  let totalQuestsCompleted = 0;
  const { isWishlisted, addToWishlist, removeFromWishlist } =
    useWishlistHandler(gameData.details.game_id);

  const [inProgressQuest, setInProgressQuest] = useState<
    CampaignQuestState | undefined
  >(undefined);

  useEffect(() => {
    if (
      isWishlisted &&
      inProgressQuest &&
      inProgressQuest.type === "wishlist"
    ) {
      invalidateQuestCompletions(
        (prevData) => {
          const newData = { ...prevData };
          newData[inProgressQuest.externalId] = {
            latest_completion_time: new Date().toISOString(),
            completion_count: 1,
            external_id: inProgressQuest.externalId,
            game_id: inProgressQuest.gameId,
            latest_rewards: [
              {
                amount: inProgressQuest.gems,
                type: "point",
              },
            ],
            total_rewards: [
              {
                amount: inProgressQuest.gems,
                type: "point",
              },
            ],
          };
          return newData;
        },
        { revalidate: false },
      );
    }
  }, [isWishlisted, inProgressQuest, invalidateQuestCompletions]);

  const incompleteQuests: CampaignQuestState[] = [];
  const allQuests = enrichedGameData.quests
    .map((quest) => {
      const completion = completedQuests?.find(
        (completion) => completion.external_id === quest.externalId,
      );
      if (completion) {
        totalQuestsCompleted++;
      }
      const state = {
        ...quest,
        linkUrl:
          quest.type === "in_game" && !(quest as CampaignQuestState).linkUrl
            ? gameData.details.landing_page_url
            : (quest as CampaignQuestState).linkUrl,
        completed: !!completion,
        completionCount: completion?.completion_count ?? 0,
        latestCompletion: completion?.latest_completion_time,
        completedToday: questCompletedInLatestPeriod(
          quest.validFrom ?? "",
          quest.refreshPeriodHours,
          completion,
        ),
      };

      // completedToday is being used as a proxy for completed this time period
      if (!state.completedToday) {
        incompleteQuests.push(state);
      }
      return state;
    })
    .sort((a, b) => {
      // Sort quests that have been completed today to the bottom
      // while maintaining the existing order within each group
      if (a.completedToday && !b.completedToday) {
        return 1; // a goes after b
      }
      if (!a.completedToday && b.completedToday) {
        return -1; // a goes before b
      }
      // If both have the same completedToday status, maintain original order
      return 0;
    });

  const completeQuest = useCallback(
    async (quest: CampaignQuest): Promise<boolean> => {
      switch (quest.type) {
        case "follow_x_account":
        case "post_x_tweet":
        case "retweet_and_like":
        case "join_discord_server":
        case "join_telegram_chat":
        case "app_store_image_validation":
        case "steam_wishlist":
          invalidateQuestCompletions(
            (prevData) => {
              const newData = { ...prevData };
              newData[quest.externalId] = {
                latest_completion_time: new Date().toISOString(),
                completion_count: 1,
                external_id: quest.externalId,
                game_id: quest.gameId,
                latest_rewards: [
                  {
                    amount: quest.gems,
                    type: "point",
                  },
                ],
                total_rewards: [
                  {
                    amount: quest.gems,
                    type: "point",
                  },
                ],
              };
              return newData;
            },
            { revalidate: false },
          );

          setInProgressQuest(undefined);
          return true;
        case "no_validate":
        case "website":
        case "claim":
        case "trailer": {
          const error = await submitQuestCompletion(
            `${quest.gameId}-${quest.externalId}-completed`,
            getAccessToken,
          );
          if (error) {
            return false;
          }
          invalidateQuestCompletions();
          return true;
        }
        default:
          return false;
      }
    },
    [invalidateQuestCompletions, getAccessToken],
  );

  const onQuestClick = async (quest: CampaignQuestState) => {
    if (!isWishlisted && quest.type !== "wishlist") {
      return;
    }

    switch (quest.type) {
      case "join_telegram_chat":
        if (quest.linkUrl) {
          window.open(quest.linkUrl, "_blank");
          setInProgressQuest(quest);
        }
        return;
      case "post_x_tweet":
      case "retweet_and_like":
        localStorage.removeItem("social_auth_error");
        localStorage.removeItem("social_quest_error");
        setInProgressQuest(quest);
        return;
      case "follow_x_account":
      case "join_discord_server":
      case "steam_wishlist":
        if (linkedAccountPlatforms.get(mapCampaignQuestType(quest.type))) {
          localStorage.removeItem(quest.externalId);
          setInProgressQuest(quest);
          openAuthorizeUrl(
            quest.externalId,
            mapCampaignQuestType(quest.type),
            quest.linkUrl || "",
            enrichedGameData.details.name,
            enrichedGameData.details.game_id,
            undefined, // loginOnly
            undefined, // tweetText
            undefined, // twitterHandle
            undefined, // tweetImageId
            quest.linkUrl, // linkUrl
          );
        }
        return;
      case "campaign_referrer":
      case "app_store_image_validation":
      case "social_engagement":
      case "claim":
      case "trailer":
        setInProgressQuest(quest);
        return;
      case "no_validate":
      case "website":
        window.open(quest.linkUrl, "_blank");
        completeQuest(quest);
        return;
      case "wishlist":
        setInProgressQuest(quest);
        addToWishlist();
        return;
      case "in_game":
        window.open(quest.linkUrl, "_blank");
        return;
    }
  };

  return {
    gameData: enrichedGameData,
    completedQuests,
    totalQuestsCompleted,
    incompleteQuests,
    allQuests,
    completeQuest,
    onQuestClick,
    inProgressQuest,
    setInProgressQuest,
    isWishlisted,
    toggleWishlist: async () => {
      if (isWishlisted) {
        await removeFromWishlist();
      } else {
        await addToWishlist();
      }
    },
    addToWishlist,
  };
};
